/*-------------------------------------------*/
/*----------IMPORTS AND INITIALIZERS---------*/
/*-------------------------------------------*/

// Imports
import { 
    Button, Card, CardContent, 
    CardHeader, Divider, Grid 
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import styled from 'styled-components';
import { Form } from "react-bootstrap";

/*-------------------------------------------*/
/*------------PRE-CONFIG INTERFACES----------*/
/*-------------------------------------------*/

// For styling react-bootstrap form.control field(s)
const StyledFormControl = styled(Form.Control)`
    height: 40px;
    border-color: lightgray;
    &:focus {
        box-shadow: none;
    }
`;

// Interface for defining filters configuration type
interface KeyValueType {
    [key: string]: any;
}

// Part of type definition for props
interface Props {
    open: boolean, drawerWidth: number,
    showSkeleton: boolean, setShowSkeleton: (bool: boolean) => void,
    fetchedFilteredRecords: KeyValueType[], setFetchedFilteredRecords: (result: KeyValueType[]) => void,
    openTableCard: boolean, setOpenTableCard: (openTableCard: boolean) => void,
    secondarySearchRecords: KeyValueType[], setSecondarySearchRecords: (result: KeyValueType[]) => void,
    recordsToRender: KeyValueType[], setRecordsToRender: (result: KeyValueType[]) => void,
    tablePageNumber: boolean, setTablePageNumber: (bool: boolean) => void
}

/*------------------------------------------*/
/*-----------------COMPONENT----------------*/
/*------------------------------------------*/

const FiltersPanel: React.FC<Props> = ({
    open, drawerWidth,
    showSkeleton, setShowSkeleton,
    fetchedFilteredRecords, setFetchedFilteredRecords,
    openTableCard, setOpenTableCard,
    secondarySearchRecords, setSecondarySearchRecords,
    recordsToRender, setRecordsToRender,
    tablePageNumber, setTablePageNumber
}) => {

    /*--------------------*/
    /*-STATES AND METHODS-*/
    /*--------------------*/

    const [tMobileNumber, setTMobileNumber] = useState("")
    const [didNumber, setDidNumber] = useState("")
    const [tMobileEntId, setTMobileEntId] = useState("")
    const [entName, setEntName] = useState("")

    // For tracking if Search button was clicked or not (with none of the filters being empty)
    const [searchButtonClicked, setSearchButtonClicked] = useState<boolean>(false)

    // Function to reorder object keys
    const reorderKeys = (obj: KeyValueType, order: string[]): KeyValueType => {
        const reorderedObj: KeyValueType = {};
        order.forEach(key => {
            if (key in obj) {
                reorderedObj[key] = obj[key];
                console.log(reorderedObj[key])
            }
        });
        return reorderedObj;
    }

    // For sending selected filter values to server for processing, on button click
    const handleSelectedFilterValues = () => {

        if (!(tMobileNumber === "" && didNumber === "" && tMobileEntId === "" && entName === "")) {
    
            setRecordsToRender([])
            setSearchButtonClicked(true)
            const url = `https://${process.env.REACT_APP_HOST_NAME}/${process.env.REACT_APP_RETRIEVE_RECORDS_ROUTE}?tmobile_number=${encodeURIComponent(tMobileNumber)}&did_number=${encodeURIComponent(didNumber)}&tmobile_ent_id=${encodeURIComponent(tMobileEntId)}&ent_name=${encodeURIComponent(entName.toLowerCase())}&queryType=1`
            
            axios.get(url)
                .then(res => {
                    const parsedResponse: KeyValueType[] = JSON.parse(res.data)
                    const order = ["tmobile_number", "did_number", "ent_name", "tmobile_ent_id", "suspended_status", "bundle_name", "addons"]
                    const reorderedResponse = parsedResponse.map(obj => reorderKeys(obj, order));
                    setFetchedFilteredRecords(reorderedResponse)
                })
                .catch(err => console.log(err))
        }
    }

    // Once searchButtonClicked is set, enable skeleton loader rendering
    useEffect(() => {
        if (searchButtonClicked === true) setShowSkeleton(true)
        console.log(`searchButtonClicked: ${searchButtonClicked}`)
    }, [searchButtonClicked])

    useEffect(() => {
        console.log(fetchedFilteredRecords)
    }, [fetchedFilteredRecords])

    // Once skeleton has been set to true, open the table
    useEffect(() => {
        if (showSkeleton === true && openTableCard === false)
            setOpenTableCard(true)
    }, [showSkeleton])

    // Once results are received, prepare for rendering them in the table
    useEffect(() => {
        setTablePageNumber(false)
        setSecondarySearchRecords([])
        setRecordsToRender(fetchedFilteredRecords)
        setShowSkeleton(false)
        setSearchButtonClicked(false)
    }, [fetchedFilteredRecords])

    /*------------------*/
    /*-RENDER COMPONENT-*/
    /*------------------*/

    return (

        // Render the FiltersPanel card
        <Card 
            sx={{ 
                boxShadow: 'none',
                border: "1px solid lightgray",
                maxWidth: open ? `calc(100vw - ${drawerWidth}px)` : `calc(200vw - ${drawerWidth}px)`
            }}
        >

            <CardHeader 
                title = 'Filters'
                titleTypographyProps={{ variant: 'h6', sx: { fontWeight: 'bold' }}}
            />

            <Divider sx={{ bgcolor: '#1e1d31' }}/>
            <CardContent>

                {/* Render form */}
                <form>

                    {/* Render filter options */}
                    <Grid container spacing={3}>
                        
                        {/* Render Ptn form field */}
                        <Grid item>
                            <Form.Group controlId="tMobileNumber">
                                <Form.Label>TMobile Number</Form.Label>
                                <StyledFormControl 
                                    type="string" 
                                    value={tMobileNumber} 
                                    onChange={(e) => setTMobileNumber(e.target.value)}
                                    placeholder="Enter T-Mobile number"
                                />
                            </Form.Group>
                        </Grid>
                        
                        {/* Render Did form field */}
                        <Grid item>
                            <Form.Group controlId="didNumber">
                                <Form.Label>DID Number</Form.Label>
                                <StyledFormControl
                                    type="string" 
                                    value={didNumber} 
                                    onChange={(e) => setDidNumber(e.target.value)}
                                    placeholder="Enter DID number"
                                />
                            </Form.Group>
                        </Grid>
                        
                        {/* Render Org Id form field */}
                        <Grid item>
                            <Form.Group controlId="tMobileEntId">
                                <Form.Label>Enterprise Id</Form.Label>
                                <StyledFormControl
                                    type="string" 
                                    value={tMobileEntId} 
                                    onChange={(e) => setTMobileEntId(e.target.value)}
                                    placeholder="Enter organization Id"
                                />
                            </Form.Group>
                        </Grid>
                        
                        {/* Render Org Name form field */}
                        <Grid item>
                            <Form.Group controlId="entName">
                                <Form.Label>Enterprise Name</Form.Label>
                                <StyledFormControl 
                                    type="string" 
                                    value={entName} 
                                    onChange={(e) => setEntName(e.target.value)}
                                    placeholder="Enter organization name"
                                >
                                </StyledFormControl>
                            </Form.Group>
                        </Grid>
                        
                    </Grid>

                    {/* Render button to filter and fetch data */}
                    <Grid 
                        container 
                        spacing={2} 
                        justifyContent="flex-end" 
                        paddingTop='20px'
                    >

                        {/* Render Reset button */}
                        <Grid item>
                            <Button 
                                variant='contained' 
                                onClick={() => {
                                    setTMobileNumber("")
                                    setDidNumber("")
                                    setTMobileEntId("")
                                    setEntName("")
                                }}
                                className='fw-bold'
                                sx={{ bgcolor: '#b3b3b3', '&:hover': { bgcolor: '#c4c4c4' } }}
                            >
                                Reset
                            </Button>
                        </Grid>

                        {/* Render Search button */}
                        <Grid item>
                            <Button 
                                variant='contained' 
                                onClick={() => {
                                    handleSelectedFilterValues()
                                }}
                                className='fw-bold'
                                sx={{ bgcolor: '#52694f', '&:hover': { bgcolor: '#73946f' } }}
                            >
                                Search
                            </Button>
                        </Grid>

                    </Grid>

                </form>
                
            </CardContent> 
        </Card>
    );
}

export default FiltersPanel;